import { useQuery, UseQueryResult } from '@edreamsodigeo/frontend-api-client';
import {
  useLogFrontendApiError,
  useDatalayerTracking,
} from '@edreamsodigeo/frontend-core';

import {
  GetAccommodationRecommendationsByIataCodeQuery,
  GetAccommodationRecommendationsByIataCodeQueryVariables,
  Query,
} from 'generated/graphql';
import React, { createContext, useEffect, useState } from 'react';
import { GET_ACCOMMODATION_RECOMMENDATIONS_BY_IATA_CODE } from '../operations/getAccommodationRecommendationsByIataCode';
import { usePerformedSearchRequest } from '../SearchRequestProvider';
import { useSearchResponseQuery } from '../SearchResponseProvider';

type AccommodationRecommendationsProviderType = Partial<
  UseQueryResult<
    GetAccommodationRecommendationsByIataCodeQuery,
    GetAccommodationRecommendationsByIataCodeQueryVariables
  >
>;
const DEFAULT_STATE = { loading: false, data: null, errors: null };
const AccommodationRecommendationsContext =
  createContext<AccommodationRecommendationsProviderType>(DEFAULT_STATE);
export const FLEXIBLE_DATES_NUMBER_OF_DAYS = 3;
export const FLEXIBLE_DATES_PRICE_FRESHNESS_DAYS = 5;

const accommodationDataLayerUpdaterParser = ({
  hotels_search_id,
}: {
  hotels_search_id?: string;
}): { hotels_search_id?: string } => {
  return {
    ...(hotels_search_id && { hotels_search_id }),
  };
};

export const AccommodationRecommendationsProvider: React.FC = ({
  children,
}) => {
  const [previousDestination, setPreviousDestination] = useState<string>();
  const { itinerary } = usePerformedSearchRequest() || {};
  const iataCode = itinerary?.segments?.[0]?.destination?.cityIata ?? '';
  const { data } = useSearchResponseQuery();
  const { save: saveDataLayer } = useDatalayerTracking();

  const skipAccommodationRecommendationsQuery =
    previousDestination === iataCode || !data;

  useEffect(() => {
    if (data && iataCode && iataCode !== previousDestination) {
      setPreviousDestination(iataCode);
    }
  }, [iataCode, data, previousDestination]);

  const accommodationRecommendationsQuery = useQuery<
    Pick<Query, 'getAccommodationRecommendationsByIataCode'>,
    GetAccommodationRecommendationsByIataCodeQueryVariables
  >(GET_ACCOMMODATION_RECOMMENDATIONS_BY_IATA_CODE, {
    variables: {
      accommodationRecommendationByIataCodeRequest: {
        iataCode,
      },
    },
    skip: skipAccommodationRecommendationsQuery,
  });

  useLogFrontendApiError(accommodationRecommendationsQuery);

  const { data: accommodationRecommendationsData } =
    accommodationRecommendationsQuery;

  useEffect(() => {
    if (accommodationRecommendationsData) {
      const hotels_search_id =
        accommodationRecommendationsData.getAccommodationRecommendationsByIataCode.propertyMappingIds
          ?.map((item) => item.id)
          .join(', ');
      saveDataLayer(accommodationDataLayerUpdaterParser({ hotels_search_id }));
    }
  }, [accommodationRecommendationsData, saveDataLayer]);

  return (
    <AccommodationRecommendationsContext.Provider
      value={accommodationRecommendationsQuery}
    >
      {children}
    </AccommodationRecommendationsContext.Provider>
  );
};

export default AccommodationRecommendationsProvider;
